/**
 * Authored track → runtime LevelData. Pure and deterministic: the same
 * document (in any object order) compiles to byte-identical output, and the
 * gameplay fingerprint changes only when something the movement rules can
 * observe changes (EDITOR_ARCHITECTURE.md, LEVEL_FORMAT.md).
 */
import { MAX_COLLIDER_HALF_EXTENT, tiledBox } from '../world/LevelBuilders';
import type { LevelData, LightDef, SolidDef, SolidShape, SpawnPoint, TriggerDef, Vec3Tuple } from '../world/LevelData';
import { validateLevelData } from '../world/LevelData';
import { BUILTIN_MATERIALS, MaterialRegistry, type SurfaceMaterialDef } from '../world/SurfaceMaterials';
import { sha256Hex } from './sha256';
import { generateShape } from './shapes';
import { COMPILER_VERSION, LIMITS, isGeometryType, param, type TrackDocument, type TrackObject } from './TrackDocument';
import { IDENTITY, canon, compose, quatFromEulerDeg, quatToEulerDeg, rotateVec, yawDegOf, type RigidTransform } from './transform';

export interface Diagnostic {
  /** The authored object the message is about, when there is one. */
  objectId?: string;
  message: string;
}

export interface TestSpawn {
  id: string;
  name: string;
  spawn: SpawnPoint;
}

export interface CompileResult {
  /** Null when there are errors. */
  level: LevelData | null;
  /** Compiled solid/trigger id → authored object id. */
  provenance: ReadonlyMap<string, string>;
  /** Authored object id → its compiled solids (world space), for the editor view and picking. */
  solidsByObject: ReadonlyMap<string, readonly SolidDef[]>;
  /** Editor-only playtest starts; never in the level or the fingerprint. */
  testSpawns: readonly TestSpawn[];
  warnings: readonly Diagnostic[];
  errors: readonly Diagnostic[];
  /** SHA-256 of the gameplay-relevant compiled content ('' when there are errors). */
  fingerprint: string;
}

/** Tile size used when splitting oversized boxes (below the hard cap so tiles keep a margin). */
const TILE_HALF = 8;
const c3 = (v: Vec3Tuple): Vec3Tuple => [canon(v[0]), canon(v[1]), canon(v[2])];
const add = (a: Vec3Tuple, b: Vec3Tuple): Vec3Tuple => [a[0] + b[0], a[1] + b[1], a[2] + b[2]];

/** World transforms through the group chain (references were checked by the validator). */
function worldTransforms(objects: readonly TrackObject[]): Map<string, RigidTransform> {
  const byId = new Map(objects.map((o) => [o.id, o]));
  const out = new Map<string, RigidTransform>();
  const resolve = (o: TrackObject, depth: number): RigidTransform => {
    const cached = out.get(o.id);
    if (cached) return cached;
    const local: RigidTransform = { position: o.position, rotation: quatFromEulerDeg(o.rotationDeg) };
    const parent = o.parent !== undefined ? byId.get(o.parent) : undefined;
    const world = parent && depth <= LIMITS.maxDepth ? compose(resolve(parent, depth + 1), local) : compose(IDENTITY, local);
    out.set(o.id, world);
    return world;
  };
  for (const o of objects) resolve(o, 0);
  return out;
}

const outsideLimit = (v: readonly number[]): boolean => v.some((x) => !(Math.abs(x) <= LIMITS.maxCoord));

/**
 * Splits a shape that exceeds the collider cap, in its own frame. Returns null
 * when it can't be split. Every parameter can be in range and still produce a
 * huge piece (a 180° facet has an infinite mitre), so the size and the number
 * of tiles are checked before any tile is built.
 */
function splitOversized(shape: SolidShape): { shape: SolidShape; offset: Vec3Tuple }[] | null {
  if (shape.type === 'box') {
    if (shape.halfExtents.every((h) => h <= MAX_COLLIDER_HALF_EXTENT)) return [{ shape, offset: [0, 0, 0] }];
    const tiles = shape.halfExtents.reduce((n, h) => n * Math.ceil(h / TILE_HALF), 1);
    if (!(tiles <= LIMITS.maxSolids)) return null;
    return tiledBox('t', [0, 0, 0], shape.halfExtents, 'x', TILE_HALF).map((t) => ({ shape: t.shape, offset: t.position }));
  }
  if (shape.type === 'cylinder') {
    if (shape.radius > MAX_COLLIDER_HALF_EXTENT) return null;
    const n = Math.max(1, Math.ceil(shape.halfHeight / TILE_HALF));
    const hh = shape.halfHeight / n;
    return Array.from({ length: n }, (_, i) => ({ shape: { type: 'cylinder', radius: shape.radius, halfHeight: hh }, offset: [0, -shape.halfHeight + hh * (2 * i + 1), 0] as Vec3Tuple }));
  }
  return shape.halfExtents.every((h) => h <= MAX_COLLIDER_HALF_EXTENT) ? [{ shape, offset: [0, 0, 0] }] : null;
}

function canonShape(s: SolidShape): SolidShape {
  return s.type === 'cylinder' ? { type: 'cylinder', radius: canon(s.radius), halfHeight: canon(s.halfHeight) } : { type: s.type, halfExtents: c3(s.halfExtents) };
}

interface GeometryEntry {
  worldKey: string;
  solids: SolidDef[];
  errors: Diagnostic[];
  warnings: Diagnostic[];
}

/**
 * Compiled geometry per authored object. Documents are immutable and editor
 * operations keep untouched objects by reference, so an object whose world
 * transform is also unchanged compiles to exactly the cached result. Measured
 * (tests/bench/authoring.test.ts): this removes most of the per-edit cost of
 * re-generating thousands of unchanged pieces.
 */
const geometryCache = new WeakMap<TrackObject, GeometryEntry>();
const worldKeyOf = (w: RigidTransform): string => `${w.position.join(',')}|${w.rotation.x},${w.rotation.y},${w.rotation.z},${w.rotation.w}`;

function compileGeometry(o: TrackObject, world: RigidTransform, errors: Diagnostic[], warnings: Diagnostic[]): SolidDef[] {
  const worldKey = worldKeyOf(world);
  let entry = geometryCache.get(o);
  if (!entry || entry.worldKey !== worldKey) {
    const e: Diagnostic[] = [];
    const w: Diagnostic[] = [];
    entry = { worldKey, solids: buildGeometry(o, world, e, w), errors: e, warnings: w };
    geometryCache.set(o, entry);
  }
  errors.push(...entry.errors);
  warnings.push(...entry.warnings);
  return entry.solids;
}

function buildGeometry(o: TrackObject, world: RigidTransform, errors: Diagnostic[], warnings: Diagnostic[]): SolidDef[] {
  const { solids, warnings: w } = generateShape(o);
  for (const message of w) warnings.push({ objectId: o.id, message });
  const out: SolidDef[] = [];
  const material = o.material ?? 'safe.stone';
  for (const s of solids) {
    const solidWorld = compose(world, { position: s.position, rotation: s.rotation });
    const extents = s.shape.type === 'cylinder' ? [s.shape.radius, s.shape.halfHeight] : s.shape.halfExtents;
    if (outsideLimit(extents) || outsideLimit(solidWorld.position)) {
      errors.push({ objectId: o.id, message: `${o.type} "${o.name}": a piece reaches past the ±${LIMITS.maxCoord} m build limit (raise Segments, soften sharp path corners, or move it closer to the origin)` });
      return [];
    }
    const pieces = splitOversized(s.shape);
    if (!pieces) {
      errors.push({ objectId: o.id, message: `${o.type} "${o.name}": a ${s.shape.type} piece is too large to split into colliders (more than ${MAX_COLLIDER_HALF_EXTENT} m half-size for a wedge or cylinder radius, or more than ${LIMITS.maxSolids} tiles); make it smaller or raise Segments` });
      return [];
    }
    if (out.length + pieces.length > LIMITS.maxSolids) {
      errors.push({ objectId: o.id, message: `${o.type} "${o.name}" alone compiles to more than ${LIMITS.maxSolids} collision pieces; lower its size or segment count` });
      return [];
    }
    for (const piece of pieces) {
      const position = add(solidWorld.position, rotateVec(solidWorld.rotation, piece.offset));
      const rotationDeg = c3(quatToEulerDeg(solidWorld.rotation));
      const def: SolidDef = { id: `${o.id}#${out.length}`, shape: canonShape(piece.shape), position: c3(position), material };
      out.push(rotationDeg.some((r) => r !== 0) ? { ...def, rotationDeg } : def);
    }
  }
  return out;
}

/** Triggers are axis-aligned boxes; only quarter-turn yaw is representable (it swaps X and Z). */
function compileTrigger(o: TrackObject, world: RigidTransform, warnings: Diagnostic[]): TriggerDef {
  const yaw = yawDegOf(world.rotation);
  const quarter = Math.round(yaw / 90);
  const swap = Math.abs(quarter) % 2 === 1;
  const up = rotateVec(world.rotation, [0, 1, 0]);
  if (Math.abs(yaw - quarter * 90) > 0.5 || up[1] < 0.9999) {
    warnings.push({ objectId: o.id, message: `${o.type} "${o.name}": zones are axis-aligned, so only quarter-turn yaw is applied to the box (the respawn facing uses the exact yaw)` });
  }
  const sx = param<number>(o, 'sizeX');
  const sy = param<number>(o, 'sizeY');
  const sz = param<number>(o, 'sizeZ');
  const half: Vec3Tuple = swap ? [sz / 2, sy / 2, sx / 2] : [sx / 2, sy / 2, sz / 2];
  const kind = o.type as TriggerDef['kind'];
  const def: TriggerDef = { id: o.id, kind, center: c3(add(world.position, [0, sy / 2, 0])), halfExtents: c3(half), material: `trigger.${kind}` };
  if (kind !== 'checkpoint') return def;
  return { ...def, checkpointIndex: param<number>(o, 'index'), spawn: spawnOf(world) };
}

const spawnOf = (world: RigidTransform): SpawnPoint => ({ position: c3(world.position), yawDeg: canon(yawDegOf(world.rotation)) });
const byId = <T extends { id: string }>(a: T, b: T): number => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);

/** Track rules the runtime validator doesn't phrase in authoring terms. */
function checkStructure(doc: TrackDocument, triggers: readonly TriggerDef[], spawns: readonly TrackObject[], world: ReadonlyMap<string, RigidTransform>, errors: Diagnostic[]): void {
  if (spawns.length !== 1) errors.push({ message: spawns.length ? `a track needs exactly one Player spawn (found ${spawns.length})` : 'add a Player spawn' });
  const starts = triggers.filter((t) => t.kind === 'start');
  if (starts.length !== 1) errors.push({ message: starts.length ? `a track needs exactly one Start zone (found ${starts.length})` : 'add a Start zone' });
  if (!triggers.some((t) => t.kind === 'finish')) errors.push({ message: 'add at least one Finish zone' });
  const cps = triggers.filter((t) => t.kind === 'checkpoint');
  if (cps.length > LIMITS.maxCheckpoints) errors.push({ message: `at most ${LIMITS.maxCheckpoints} checkpoints` });
  const seen = new Map<number, string>();
  for (const t of cps) {
    const prev = seen.get(t.checkpointIndex!);
    if (prev !== undefined) errors.push({ objectId: t.id, message: `checkpoints "${prev}" and "${t.id}" share order ${t.checkpointIndex}` });
    seen.set(t.checkpointIndex!, t.id);
  }
  for (let i = 0; i < seen.size; i++) {
    if (!seen.has(i)) {
      errors.push({ message: `checkpoint orders must run 0, 1, 2 … without gaps (order ${i} is missing)` });
      break;
    }
  }
  const spawn = spawns[0];
  if (spawn && world.get(spawn.id)!.position[1] <= doc.killPlaneY) errors.push({ objectId: spawn.id, message: 'the Player spawn is below the kill plane' });
}

function customMaterials(doc: TrackDocument): SurfaceMaterialDef[] {
  return doc.materials.map((m) => ({ id: m.id, kind: m.kind, behaviors: m.behaviors.map((b) => ({ ...b })), visual: { ...m.visual } }));
}

/** Everything the movement rules can observe, in a fixed key order. Appearance, names and editor state are excluded. */
function gameplayFingerprint(level: Omit<LevelData, 'contentHash'>, ruleset: string): string {
  const mats = (level.materials ?? []).map((m) => ({ id: m.id, kind: m.kind, behaviors: m.behaviors }));
  const payload = {
    compiler: COMPILER_VERSION,
    ruleset,
    killPlaneY: level.killPlaneY,
    start: level.start,
    materials: mats,
    solids: level.solids.map((s) => ({ id: s.id, shape: s.shape, position: s.position, rotationDeg: s.rotationDeg ?? null, material: s.material })),
    triggers: level.triggers.map((t) => ({ id: t.id, kind: t.kind, center: t.center, halfExtents: t.halfExtents, checkpointIndex: t.checkpointIndex ?? null, spawn: t.spawn ?? null })),
  };
  return sha256Hex(JSON.stringify(payload));
}

export interface CompileOptions {
  /**
   * Geometry only, for redrawing while a gizmo drag is in progress: skips the
   * runtime level validation and the fingerprint (about 40 % of a full compile
   * at 2000 objects). The result has no level and must not be used for play,
   * records or validation; the editor compiles fully when the drag ends.
   */
  preview?: boolean;
}

/** Compiles a validated document. Never throws for document content; problems come back as diagnostics. */
export function compileTrack(doc: TrackDocument, options: CompileOptions = {}): CompileResult {
  const errors: Diagnostic[] = [];
  const warnings: Diagnostic[] = [];
  const provenance = new Map<string, string>();
  const solidsByObject = new Map<string, SolidDef[]>();
  const world = worldTransforms(doc.objects);
  const solids: SolidDef[] = [];
  const triggers: TriggerDef[] = [];
  const lights: (LightDef & { id: string })[] = [];
  const labels: { id: string; text: string; position: Vec3Tuple }[] = [];
  const spawns: TrackObject[] = [];
  const testSpawns: TestSpawn[] = [];

  for (const o of doc.objects) {
    const w = world.get(o.id)!;
    if (outsideLimit(w.position)) {
      // Each local position is in range; a chain of groups can still add up past the limit.
      errors.push({ objectId: o.id, message: `${o.type} "${o.name}" is placed past the ±${LIMITS.maxCoord} m build limit` });
      continue;
    }
    if (isGeometryType(o.type)) {
      // Stop generating once the track is over budget: the error is already certain.
      if (solids.length > LIMITS.maxSolids) continue;
      const out = compileGeometry(o, w, errors, warnings);
      solidsByObject.set(o.id, out);
      for (const s of out) provenance.set(s.id, o.id);
      solids.push(...out);
      continue;
    }
    switch (o.type) {
      case 'start':
      case 'finish':
      case 'checkpoint':
        triggers.push(compileTrigger(o, w, warnings));
        provenance.set(o.id, o.id);
        break;
      case 'spawn':
        spawns.push(o);
        break;
      case 'testSpawn':
        testSpawns.push({ id: o.id, name: o.name, spawn: spawnOf(w) });
        break;
      case 'light':
        lights.push({ id: o.id, position: c3(w.position), color: param<number>(o, 'color'), intensity: param<number>(o, 'intensity'), distance: param<number>(o, 'distance') });
        break;
      case 'label':
        labels.push({ id: o.id, text: param<string>(o, 'text'), position: c3(w.position) });
        break;
      case 'group':
        break;
    }
  }
  if (solids.length > LIMITS.maxSolids) errors.push({ message: `the track compiles to ${solids.length} collision pieces (limit ${LIMITS.maxSolids}); lower segment counts or sizes` });
  checkStructure(doc, triggers, spawns, world, errors);

  solids.sort(byId);
  triggers.sort(byId);
  lights.sort(byId);
  labels.sort(byId);
  const spawnObj = spawns[0];
  const level: Omit<LevelData, 'contentHash'> = {
    id: doc.id,
    name: doc.title || 'Untitled track',
    version: COMPILER_VERSION,
    materials: customMaterials(doc),
    solids,
    triggers,
    start: spawnObj ? spawnOf(world.get(spawnObj.id)!) : { position: [0, 0, 0], yawDeg: 0 },
    killPlaneY: canon(doc.killPlaneY),
    lights: lights.map((l) => ({ position: l.position, color: l.color, intensity: l.intensity, distance: l.distance })),
    labels: labels.map((l) => ({ text: l.text, position: l.position })),
    ...(doc.environment.preset === 'violet-dusk' ? {} : { atmosphere: doc.environment.preset }),
  };

  if (options.preview) return { level: null, provenance, solidsByObject, testSpawns, warnings, errors, fingerprint: '' };
  if (!errors.length) {
    let registry: MaterialRegistry | null = null;
    try {
      registry = new MaterialRegistry([...BUILTIN_MATERIALS, ...(level.materials ?? [])]);
    } catch (err: unknown) {
      errors.push({ message: err instanceof Error ? err.message : String(err) });
    }
    if (registry) for (const message of validateLevelData(level, registry)) errors.push({ objectId: provenance.get(message.match(/^(?:solid|trigger|checkpoint) (\S+?):/)?.[1] ?? ''), message });
  }
  if (errors.length) return { level: null, provenance, solidsByObject, testSpawns, warnings, errors, fingerprint: '' };
  const fingerprint = gameplayFingerprint(level, doc.ruleset);
  return { level: { ...level, contentHash: fingerprint }, provenance, solidsByObject, testSpawns, warnings, errors, fingerprint };
}
